import { hasChartData, parseStatValue } from './dashboardStatUtils';
import { VIBRANT_PALETTE } from './dashboardChartColors';

const C = VIBRANT_PALETTE;

const buildSeries = (getStat, items) =>
    items.map((item, index) => ({
        ...item,
        color: item.color || C[index % C.length],
        value: parseStatValue(getStat(item.key)),
    }));

export const OVERVIEW_CHARTS = [
    {
        id: 'communications-channels',
        title: 'Communication Channels',
        subtitle: 'Email, SMS & calls — last 30 days',
        icon: 'bi-broadcast',
        accent: C[0],
        type: 'polar',
        build: (getStat) => ({
            series: buildSeries(getStat, [
                { key: 'EMAIL_SENT', label: 'Email' },
                { key: 'SMS_SENT', label: 'SMS' },
                { key: 'CALL_SENT', label: 'Calls' },
            ]),
        }),
    },
    {
        id: 'patient-demographics',
        title: 'Patient Demographics',
        subtitle: 'Age group distribution',
        icon: 'bi-people-fill',
        accent: C[4],
        type: 'pie',
        build: (getStat) => ({
            series: buildSeries(getStat, [
                { key: 'PATIENTS_CHILDREN', label: 'Children' },
                { key: 'PATIENTS_TEENAGER', label: 'Teenagers' },
                { key: 'PATIENTS_ADULT', label: 'Adults' },
                { key: 'PATIENTS_SENIOR', label: 'Seniors' },
                { key: 'PATIENTS_AGE_UNKNOWN', label: 'Unknown' },
            ]),
        }),
    },
    {
        id: 'appointment-status',
        title: 'Appointment Status',
        subtitle: 'Confirmed vs unconfirmed',
        icon: 'bi-calendar-check',
        accent: C[8],
        type: 'pie',
        build: (getStat) => ({
            series: buildSeries(getStat, [
                { key: 'APPOINTMENTS_CONFIRMED', label: 'Confirmed' },
                { key: 'APPOINTMENTS_UNCONFIRMED', label: 'Unconfirmed' },
            ]),
        }),
    },
    {
        id: 'reputation-feedback',
        title: 'Patient Feedback',
        subtitle: 'Sentiment breakdown',
        icon: 'bi-emoji-smile',
        accent: C[2],
        type: 'donut',
        build: (getStat) => ({
            series: buildSeries(getStat, [
                { key: 'REPUTATION_FEEDBACK_POSITIVE', label: 'Positive' },
                { key: 'REPUTATION_FEEDBACK_NEUTRAL', label: 'Neutral' },
                { key: 'REPUTATION_FEEDBACK_NEGATIVE', label: 'Negative' },
            ]),
        }),
    },
];

export const SECTION_CHARTS = {
    patients: [
        {
            id: 'patient-contact-coverage',
            title: 'Contact Coverage',
            subtitle: '% of patients with contact info',
            icon: 'bi-envelope-check',
            accent: C[0],
            type: 'radial',
            percentage: true,
            build: (getStat) => ({
                series: buildSeries(getStat, [
                    { key: 'PATIENTS_EMAIL_PERCENT', label: 'Email' },
                    { key: 'PATIENTS_CELL_PHONE_PERCENT', label: 'Cell' },
                    { key: 'PATIENTS_HOME_PHONE_PERCENT', label: 'Home' },
                ]),
            }),
        },
        {
            id: 'patient-age-mix',
            title: 'Age Mix',
            subtitle: 'Patient population by age group',
            icon: 'bi-bar-chart-steps',
            accent: C[5],
            type: 'polar',
            build: (getStat) => ({
                series: buildSeries(getStat, [
                    { key: 'PATIENTS_CHILDREN', label: 'Children' },
                    { key: 'PATIENTS_TEENAGER', label: 'Teens' },
                    { key: 'PATIENTS_ADULT', label: 'Adults' },
                    { key: 'PATIENTS_SENIOR', label: 'Seniors' },
                ]),
            }),
        },
    ],
    appointments: [
        {
            id: 'reminder-delivery',
            title: 'Reminder Funnel',
            subtitle: 'Volume across reminder stages',
            icon: 'bi-bell',
            accent: C[3],
            type: 'area',
            build: (getStat) => ({
                series: buildSeries(getStat, [
                    { key: 'REMINDERS_PREBOOKED_SENT', label: 'Prebooked' },
                    { key: 'REMINDERS_FIRST_SENT', label: 'First' },
                    { key: 'REMINDERS_SECOND_SENT', label: 'Second' },
                    { key: 'REMINDERS_LAST_SENT', label: 'Last Day' },
                    { key: 'REMINDERS_SAME_DAY_SENT', label: 'Same Day' },
                ]),
            }),
        },
    ],
    communications: [
        {
            id: 'communication-failures',
            title: 'Delivery Failures',
            subtitle: 'Failed sends by channel',
            icon: 'bi-exclamation-octagon',
            accent: C[10],
            type: 'bar-horizontal',
            build: (getStat) => ({
                series: buildSeries(getStat, [
                    { key: 'EMAIL_SEND_FAILED', label: 'Email' },
                    { key: 'SMS_SEND_FAILED', label: 'SMS' },
                    { key: 'CALL_SEND_FAILED', label: 'Calls' },
                ]),
            }),
        },
        {
            id: 'reminder-types',
            title: 'Reminder Types',
            subtitle: 'Outbound reminder mix',
            icon: 'bi-layers',
            accent: C[6],
            type: 'pie',
            build: (getStat) => ({
                series: buildSeries(getStat, [
                    { key: 'FIRST_REMINDERS', label: 'First' },
                    { key: 'SECOND_REMINDERS', label: 'Second' },
                    { key: 'LAST_REMINDERS', label: 'Last Day' },
                    { key: 'SAME_DAY_REMINDERS', label: 'Same Day' },
                    { key: 'RECALL_REMINDERS', label: 'Recall' },
                ]),
            }),
        },
    ],
    gateway: [
        {
            id: 'gateway-messages',
            title: 'Gateway Message Mix',
            subtitle: 'Two-way texting activity',
            icon: 'bi-phone',
            accent: C[12],
            type: 'donut',
            build: (getStat) => ({
                series: buildSeries(getStat, [
                    { key: 'GATEWAY_MESSAGES_SENT', label: 'Sent' },
                    { key: 'GATEWAY_MESSAGES_RECEIVED', label: 'Received' },
                    { key: 'GATEWAY_MESSAGES_FAILED', label: 'Failed' },
                ]),
            }),
        },
    ],
    bookings: [
        {
            id: 'web-booking-pipeline',
            title: 'Booking Pipeline',
            subtitle: 'Online request stages',
            icon: 'bi-globe2',
            accent: C[7],
            type: 'bar-horizontal',
            build: (getStat) => ({
                series: buildSeries(getStat, [
                    { key: 'WEB_BOOKINGS_FOLLOWUP', label: 'Follow-Up' },
                    { key: 'WEB_BOOKINGS_PROCESSED', label: 'Processed' },
                    { key: 'WEB_BOOKINGS_NEW_PATIENT', label: 'New Patient' },
                    { key: 'WEB_BOOKINGS_RETURNING_PATIENT', label: 'Returning' },
                ]),
            }),
        },
    ],
    campaigns: [
        {
            id: 'campaign-status',
            title: 'Campaign Status',
            subtitle: 'Active, completed & errors',
            icon: 'bi-megaphone',
            accent: C[1],
            type: 'polar',
            build: (getStat) => ({
                series: buildSeries(getStat, [
                    { key: 'ACTIVE_CAMPAIGNS', label: 'Active' },
                    { key: 'CAMPAIGNS_COMPLETED', label: 'Completed' },
                    { key: 'CAMPAIGNS_ERROR', label: 'Errors' },
                ]),
            }),
        },
    ],
    recalls: [
        {
            id: 'recall-status',
            title: 'Recall Processing',
            subtitle: 'File processing pipeline',
            icon: 'bi-arrow-repeat',
            accent: C[9],
            type: 'pie',
            build: (getStat) => ({
                series: buildSeries(getStat, [
                    { key: 'RECALL_REQUESTS_PENDING', label: 'Pending' },
                    { key: 'RECALL_REQUESTS_IN_PROCESS', label: 'In Process' },
                    { key: 'RECALL_REQUESTS_COMPLETED', label: 'Completed' },
                    { key: 'RECALL_REQUESTS_FAILED', label: 'Failed' },
                ]),
            }),
        },
    ],
    'reputation-enhancement': [
        {
            id: 'reputation-feedback-mix',
            title: 'Feedback Sentiment',
            subtitle: 'How patients feel',
            icon: 'bi-emoji-heart-eyes',
            accent: C[2],
            type: 'polar',
            build: (getStat) => ({
                series: buildSeries(getStat, [
                    { key: 'REPUTATION_FEEDBACK_POSITIVE', label: 'Positive' },
                    { key: 'REPUTATION_FEEDBACK_NEUTRAL', label: 'Neutral' },
                    { key: 'REPUTATION_FEEDBACK_NEGATIVE', label: 'Negative' },
                ]),
            }),
        },
        {
            id: 'reputation-activity',
            title: 'Reputation Activity',
            subtitle: 'Feedback & pending comments',
            icon: 'bi-chat-square-text',
            accent: C[14],
            type: 'area',
            build: (getStat) => ({
                series: buildSeries(getStat, [
                    { key: 'REPUTATION_FEEDBACK_TOTAL', label: 'Total Feedback' },
                    { key: 'REPUTATION_COMMENTS_PENDING', label: 'Pending Comments' },
                ]),
            }),
        },
    ],
    pms: [
        {
            id: 'pms-sync',
            title: 'PMS Sync Results',
            subtitle: 'This month',
            icon: 'bi-cloud-check',
            accent: C[11],
            type: 'pie',
            build: (getStat) => ({
                series: buildSeries(getStat, [
                    { key: 'PMS_SYNC_SUCCESS_THIS_MONTH', label: 'Success' },
                    { key: 'PMS_SYNC_ERROR_THIS_MONTH', label: 'Failed' },
                ]),
            }),
        },
    ],
    staff: [
        {
            id: 'staff-roles',
            title: 'Staff by Role',
            subtitle: 'Team composition',
            icon: 'bi-person-badge',
            accent: C[15],
            type: 'donut',
            build: (getStat) => ({
                series: buildSeries(getStat, [
                    { key: 'STAFF_MANAGERS', label: 'Managers' },
                    { key: 'STAFF_ECPS', label: 'ECPs' },
                    { key: 'STAFF_MEMBERS', label: 'Staff' },
                    { key: 'STAFF_BLOCKED', label: 'Blocked' },
                ]),
            }),
        },
    ],
};

export const getSectionCharts = (sectionId, getStat) => {
    const charts = SECTION_CHARTS[sectionId] || [];

    return charts
        .map((chart, index) => {
            const data = chart.build(getStat);
            if (!hasChartData(data.series)) {
                return null;
            }
            return { ...chart, ...data, chartIndex: index };
        })
        .filter(Boolean);
};

export const getOverviewCharts = (getStat) =>
    OVERVIEW_CHARTS.map((chart, index) => ({
        ...chart,
        ...chart.build(getStat),
        chartIndex: index,
    })).filter((chart) => hasChartData(chart.series));
